import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CircularProgress } from '@mui/material';

import { useQuery } from '@tanstack/react-query';

import { AttachmentResponse, KeyVisualsResponse } from '@/api/data-contracts';
import useOrdersService from '@/features/orders/api/orders.service';
import KeyVisualsTask from '@/features/orders/ui/KeyVisualsTasks/KeyVisualsTask';
import { Container } from '@/features/orders/ui/Tasks/Tasks.styles';
import { queryKeys } from '@/react-query/constants';

import TasksAccordion from '../Tasks/TasksAccordion';

interface Props {
  orderId: string;
  attachment: AttachmentResponse;
}

const KeyVisualsTasks: FC<Props> = ({ orderId, attachment }) => {
  const { t } = useTranslation();
  const { getOrderKeyVisuals } = useOrdersService();

  const [tasksPendingAccordionExpanded, setTasksPendingAccordionExpanded] = useState<boolean>(true);
  const [tasksApprovedAccordionExpanded, setTasksApprovedAccordionExpanded] =
    useState<boolean>(true);
  const [tasksRejectedAccordionExpanded, setTasksRejectedAccordionExpanded] =
    useState<boolean>(true);

  const closeAll = useCallback(() => {
    setTasksPendingAccordionExpanded(false);
    setTasksApprovedAccordionExpanded(false);
    setTasksRejectedAccordionExpanded(false);
  }, []);

  const { data: keyVisuals, isFetching } = useQuery<KeyVisualsResponse>(
    [queryKeys.tasks, 'attachment', attachment?.id],
    ({ signal }) => getOrderKeyVisuals(orderId, signal),
    {
      enabled: !!attachment,
    },
  );

  const tasks = useMemo(
    () =>
      keyVisuals?.tasks.filter(
        (t) => t.resourceId === attachment.id && t.resourceVersion === attachment.activeVersion,
      ) ?? [],
    [attachment.activeVersion, attachment.id, keyVisuals?.tasks],
  );

  return isFetching ? (
    <CircularProgress />
  ) : keyVisuals?.tasks ? (
    <Container>
      <TasksAccordion
        label={t('Pending')}
        tasks={
          tasks
            .filter(
              (t) =>
                keyVisuals.activeKeyVisualsApprovals[t.organizationId].find(
                  (approval) =>
                    approval.resourceId === t.resourceId &&
                    approval.revisionId === t.resourceVersion,
                )?.status === 'PENDING',
            )
            ?.map((task) => ({ ...task, status: 'PENDING' })) ?? []
        }
        expanded={tasksPendingAccordionExpanded}
        setExpanded={setTasksPendingAccordionExpanded}
        closeAll={closeAll}
        renderTask={(task) => <KeyVisualsTask orderId={orderId} task={task} />}
      />

      <TasksAccordion
        label={t('Completed')}
        tasks={
          tasks
            .filter(
              (t) =>
                keyVisuals.activeKeyVisualsApprovals[t.organizationId].find(
                  (approval) =>
                    approval.resourceId === t.resourceId &&
                    approval.revisionId === t.resourceVersion,
                )?.status === 'COMPLETED',
            )
            ?.map((task) => ({ ...task, status: 'COMPLETED' })) ?? []
        }
        expanded={tasksApprovedAccordionExpanded}
        setExpanded={setTasksApprovedAccordionExpanded}
        closeAll={closeAll}
        renderTask={(task) => <KeyVisualsTask orderId={orderId} task={task} />}
      />

      <TasksAccordion
        label={t('Rejected')}
        tasks={
          tasks
            .filter(
              (t) =>
                keyVisuals.activeKeyVisualsApprovals[t.organizationId].find(
                  (approval) =>
                    approval.resourceId === t.resourceId &&
                    approval.revisionId === t.resourceVersion,
                )?.status === 'REJECTED',
            )
            ?.map((task) => ({ ...task, status: 'REJECTED' })) ?? []
        }
        expanded={tasksRejectedAccordionExpanded}
        setExpanded={setTasksRejectedAccordionExpanded}
        closeAll={closeAll}
        renderTask={(task) => <KeyVisualsTask orderId={orderId} task={task} />}
      />
    </Container>
  ) : null;
};

export default KeyVisualsTasks;
